import React, { useEffect, useState } from 'react'
import { useEvent } from '@/hooks/useEvent'
import { supabase } from '@/lib/supabaseClient'
import type { HotelRow } from '@/lib/types'
import { Badge, Button, Card, EmptyState, Field, Input, Modal, PageSpinner, Textarea, useConfirm, useToast } from '@/components/ui'

export default function Hotels() {
  const { event } = useEvent()
  const [hotels, setHotels] = useState<HotelRow[] | null>(null)
  const [editing, setEditing] = useState<HotelRow | 'new' | null>(null)
  const { push } = useToast()
  const { confirm, confirmDialog } = useConfirm()

  async function load() {
    const { data, error } = await supabase.from('hotels').select('*').eq('event_id', event.id).order('name')
    if (error) push(error.message, 'error')
    setHotels((data as HotelRow[]) || [])
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id])

  async function remove(h: HotelRow) {
    const ok = await confirm({ title: `Delete ${h.name}?`, description: 'This only works if no rooms have been allocated at this hotel yet.', tone: 'danger', confirmLabel: 'Delete' })
    if (!ok) return
    const { error } = await supabase.from('hotels').delete().eq('id', h.id)
    if (error) {
      push('Could not delete: this hotel already has allocated rooms.', 'error')
      return
    }
    push('Hotel deleted')
    load()
  }

  if (hotels === null) return <PageSpinner label="Loading hotels…" />

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Hotels</h1>
          <p className="text-sm text-slate-500">Hotels available to allocate rooms at within this event.</p>
        </div>
        <Button variant="primary" onClick={() => setEditing('new')}>
          + Add Hotel
        </Button>
      </div>

      {hotels.length === 0 ? (
        <EmptyState title="No hotels yet" description="Add the hotels you'll be allocating rooms at." action={<Button variant="primary" onClick={() => setEditing('new')}>Add Hotel</Button>} />
      ) : (
        <div className="space-y-2">
          {hotels.map((h) => (
            <Card key={h.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900">{h.name}</span>
                  {!h.is_active && <Badge tone="slate">Inactive</Badge>}
                </div>
                <div className="text-xs text-slate-500">
                  {[h.address, h.contact_person, h.phone].filter(Boolean).join(' · ') || 'No details added'}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setEditing(h)}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(h)}>
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <HotelModal
          eventId={event.id}
          hotel={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
          }}
        />
      )}
      {confirmDialog}
    </div>
  )
}

function HotelModal({ eventId, hotel, onClose, onSaved }: { eventId: string; hotel: HotelRow | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(hotel?.name || '')
  const [address, setAddress] = useState(hotel?.address || '')
  const [contactPerson, setContactPerson] = useState(hotel?.contact_person || '')
  const [phone, setPhone] = useState(hotel?.phone || '')
  const [email, setEmail] = useState(hotel?.email || '')
  const [notes, setNotes] = useState(hotel?.notes || '')
  const [isActive, setIsActive] = useState(hotel?.is_active ?? true)
  const [saving, setSaving] = useState(false)
  const { push } = useToast()

  async function submit() {
    if (!name.trim()) {
      push('Hotel name is required', 'error')
      return
    }
    setSaving(true)
    const payload = {
      event_id: eventId,
      name: name.trim(),
      address: address.trim() || null,
      contact_person: contactPerson.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      notes: notes.trim() || null,
      is_active: isActive,
    }
    const { error } = hotel ? await supabase.from('hotels').update(payload).eq('id', hotel.id) : await supabase.from('hotels').insert(payload)
    setSaving(false)
    if (error) {
      push(error.message, 'error')
      return
    }
    push(hotel ? 'Hotel updated' : 'Hotel added')
    onSaved()
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={hotel ? 'Edit Hotel' : 'Add Hotel'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Hotel Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Hilton Alexandria" />
        </Field>
        <Field label="Address">
          <Input value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact Person">
            <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />
          </Field>
          <Field label="Phone">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
        </div>
        <Field label="Email">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-600" />
          Active
        </label>
      </div>
    </Modal>
  )
}
